/**
 * Adding or changing an approved Foundry connection (D441): a sheet from the trailing edge. An Azure connection names the
 * Entra directory and the projects people may use, each with its deployments and hosted tools; an Azure Local connection
 * names the cluster and what runs on it. Both say whose identity the work runs as, and where new resources may be created.
 * Parts the form does not show (subscriptions, resources, a project's resource id) are kept as they were.
 */

import type { FoundryConnection, FoundryConnectionInput, FoundryConnectionProject } from '@aioe/shared';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';

import { foundryRoutes, problemText, routes } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import { Button, ChoiceSelect, ErrorNote, inputClass, Tick } from '../ui.tsx';
import { FormField, FormSheet } from '../ui/FormSheet.tsx';
import { Chips } from '../manifestClauses.tsx';
import { Block } from './FormBlock.tsx';

const NO_SECRET = '__none';
const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const isHttps = (v: string) => {
  try {
    return new URL(v).protocol === 'https:';
  } catch {
    return false;
  }
};

interface ProjectDraft {
  endpoint: string;
  name: string;
  deployments: string[];
  hostedTools: string[];
  /** Kept as it was when editing. */
  resourceId?: string;
}

const blankProject = (): ProjectDraft => ({ endpoint: '', name: '', deployments: [], hostedTools: [] });

export function ConnectionForm({ connection, onClose, onSaved }: { connection?: FoundryConnection; onClose: () => void; onSaved: () => void }) {
  const editing = Boolean(connection);
  const [kind, setKind] = useState<FoundryConnection['kind']>(connection?.kind ?? 'azure');
  const [name, setName] = useState(connection?.name ?? '');
  const [tenantId, setTenantId] = useState(connection?.tenantId ?? '');
  const [projects, setProjects] = useState<ProjectDraft[]>(() => (connection?.projects ?? []).map((p) => ({ endpoint: p.endpoint, name: p.name, deployments: [...p.deployments], hostedTools: [...(p.hostedTools ?? [])], ...(p.resourceId ? { resourceId: p.resourceId } : {}) })));
  const [local, setLocal] = useState({ endpoint: connection?.azureLocal?.endpoint ?? '', cluster: connection?.azureLocal?.cluster ?? '', disconnected: connection?.azureLocal?.disconnected ?? false, models: connection?.azureLocal?.models ?? [], agents: connection?.azureLocal?.agents ?? false });
  const [identity, setIdentity] = useState<FoundryConnection['identity']>(connection?.identity ?? 'person');
  const [secret, setSecret] = useState(connection?.organisationSecret ?? NO_SECRET);
  const [regions, setRegions] = useState<string[]>(connection?.regions ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const secrets = useLoad(() => routes.secrets(), []);

  const kept = (secrets.data?.secrets ?? []).filter((s) => s.scope === 'organisation').map((s) => s.name);
  if (secret !== NO_SECRET && !kept.includes(secret)) kept.push(secret);
  const secretOptions: Record<string, string> = { [NO_SECRET]: kept.length ? 'None chosen' : 'No organisation secrets kept yet', ...Object.fromEntries(kept.sort().map((n) => [n, n])) };

  const setProject = (i: number, patch: Partial<ProjectDraft>) => setProjects((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const problem = !name.trim()
    ? 'Give the connection a name.'
    : kind === 'azure'
      ? !GUID.test(tenantId.trim())
        ? 'The directory (tenant) id is a GUID, such as 00000000-0000-0000-0000-000000000000.'
        : projects.some((p) => !isHttps(p.endpoint.trim()) || !p.name.trim())
          ? 'Each project needs its https address and a name.'
          : undefined
      : !isHttps(local.endpoint.trim()) || !local.cluster.trim()
        ? 'The cluster needs its https address and a name.'
        : undefined;

  const save = async () => {
    setBusy(true);
    setError(undefined);
    const base = {
      name: name.trim(),
      identity,
      ...(identity === 'organisation' && secret !== NO_SECRET ? { organisationSecret: secret } : {}),
      ...(regions.length ? { regions } : {}),
      ...(connection?.subscriptionIds ? { subscriptionIds: connection.subscriptionIds } : {}),
    };
    const body: FoundryConnectionInput =
      kind === 'azure'
        ? {
            kind,
            ...base,
            tenantId: tenantId.trim(),
            ...(connection?.resources ? { resources: connection.resources } : {}),
            projects: projects.map((p): FoundryConnectionProject => ({ endpoint: p.endpoint.trim(), name: p.name.trim(), deployments: p.deployments, ...(p.hostedTools.length ? { hostedTools: p.hostedTools } : {}), ...(p.resourceId ? { resourceId: p.resourceId } : {}) })),
          }
        : { kind, ...base, azureLocal: { endpoint: local.endpoint.trim(), cluster: local.cluster.trim(), disconnected: local.disconnected, ...(local.models.length ? { models: local.models } : {}), ...(local.agents ? { agents: true } : {}) } };
    try {
      if (connection) await foundryRoutes.saveConnection(connection.id, body);
      else await foundryRoutes.addConnection(body);
      onSaved();
    } catch (e) {
      setError(problemText(e));
      setBusy(false);
    }
  };

  return (
    <FormSheet
      width="xl"
      title={editing ? `Edit ${connection!.name}` : 'Add a connection'}
      description="Once approved, people can use the projects listed here from their workbench. The organisation’s Foundry policy is rewritten to match when you save."
      onClose={() => !busy && onClose()}
      footer={
        <>
          <Button variant="tertiary" onPress={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onPress={() => void save()} isPending={busy} isDisabled={Boolean(problem)}>
            {editing ? 'Save' : 'Add connection'}
          </Button>
        </>
      }
    >
      {!editing ? (
        <Block label="Where it runs">
          <Segment aria-label="Where it runs" size="sm" selectedKey={kind} onSelectionChange={(k) => setKind(k === 'azure-local' ? 'azure-local' : 'azure')} className="w-fit max-w-full">
            <Segment.Item id="azure">Azure</Segment.Item>
            <Segment.Item id="azure-local">Azure Local</Segment.Item>
          </Segment>
        </Block>
      ) : null}
      <FormField label="Name" hint="What people and administrators call this connection.">
        <input className={inputClass} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
      </FormField>

      {kind === 'azure' ? (
        <>
          <FormField label="Directory (Entra tenant) id" hint="The directory the projects sign people in to.">
            <input className={`${inputClass} mono`} value={tenantId} spellCheck={false} onChange={(e) => setTenantId(e.target.value)} />
          </FormField>
          <Block label="Projects" hint="Only these projects, with these model deployments and hosted tools, are approved.">
            <div className="grid gap-2">
              {projects.map((p, i) => (
                <div key={i} className="grid gap-3 rounded-lg border border-separator p-3">
                  <FormField label="Project address" hint="The https address of the Foundry project.">
                    <input className={`${inputClass} mono`} value={p.endpoint} spellCheck={false} onChange={(e) => setProject(i, { endpoint: e.target.value })} />
                  </FormField>
                  <FormField label="Project name">
                    <input className={inputClass} value={p.name} maxLength={100} onChange={(e) => setProject(i, { name: e.target.value })} />
                  </FormField>
                  <Block label="Model deployments" hint="Press Enter after each one.">
                    <Chips items={p.deployments} onChange={(v) => setProject(i, { deployments: v })} />
                  </Block>
                  <Block label="Hosted tools" hint="The Foundry tools agents in this project may use.">
                    <Chips items={p.hostedTools} onChange={(v) => setProject(i, { hostedTools: v })} />
                  </Block>
                  <div>
                    <Button size="sm" variant="ghost" onPress={() => setProjects((ps) => ps.filter((_, j) => j !== i))}>
                      Remove this project
                    </Button>
                  </div>
                </div>
              ))}
              <div>
                <Button size="sm" variant="secondary" onPress={() => setProjects((ps) => [...ps, blankProject()])}>
                  Add a project
                </Button>
              </div>
            </div>
          </Block>
        </>
      ) : (
        <>
          <FormField label="Cluster name">
            <input className={inputClass} value={local.cluster} maxLength={100} onChange={(e) => setLocal({ ...local, cluster: e.target.value })} />
          </FormField>
          <FormField label="Cluster address" hint="The https address workbenches call.">
            <input className={`${inputClass} mono`} value={local.endpoint} spellCheck={false} onChange={(e) => setLocal({ ...local, endpoint: e.target.value })} />
          </FormField>
          <Block label="Allowed models" hint="Press Enter after each one. Leave empty to allow every model the cluster serves.">
            <Chips items={local.models} onChange={(v) => setLocal({ ...local, models: v })} />
          </Block>
          <div className="grid gap-2">
            <Tick checked={local.disconnected} onChange={(v) => setLocal({ ...local, disconnected: v })}>
              Works disconnected
            </Tick>
            <Tick checked={local.agents} onChange={(v) => setLocal({ ...local, agents: v })}>
              Agents and tools allowed
            </Tick>
          </div>
        </>
      )}

      <Block label="Whose sign-in the work runs as">
        <Segment aria-label="Whose sign-in the work runs as" size="sm" selectedKey={identity} onSelectionChange={(k) => setIdentity(k === 'organisation' ? 'organisation' : 'person')} className="w-fit max-w-full">
          <Segment.Item id="person">Each person’s own</Segment.Item>
          <Segment.Item id="organisation">The organisation’s</Segment.Item>
        </Segment>
      </Block>
      {identity === 'organisation' ? (
        <Block label="Organisation secret" hint="The secret nodes sign in with. Keep it under Secrets, at organisation scope; only its name is stored here.">
          <ChoiceSelect label="Organisation secret" className="w-full" value={secret} options={secretOptions} onChange={setSecret} />
        </Block>
      ) : null}
      <Block label="Regions for new resources" hint="Where people may create new Foundry resources, such as australiaeast. Leave empty for no limit.">
        <Chips items={regions} onChange={setRegions} />
      </Block>
      {problem && (name || tenantId || local.cluster || projects.length) ? <p className="text-xs text-muted">{problem}</p> : null}
      <ErrorNote error={error} />
    </FormSheet>
  );
}
